import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function VerifyOtpPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { pendingAuth, verifyOtp, clearPendingAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [digits, setDigits] = useState<string[]>(["", "", "", "", "", ""]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Redirect if no pending auth
  useEffect(() => {
    if (!pendingAuth) {
      navigate("/login", { replace: true });
    }
  }, [pendingAuth, navigate]);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // Focus active input on mount and when activeIndex changes
  useEffect(() => {
    if (inputRefs.current[activeIndex]) {
      inputRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex]);

  const handleDigitChange = useCallback(
    (index: number, value: string) => {
      // Only allow single digit
      const digit = value.replace(/\D/g, "").slice(0, 1);
      if (!digit) return;

      const newDigits = [...digits];
      newDigits[index] = digit;
      setDigits(newDigits);
      setError("");

      // Move to next input
      if (index < 5) {
        setActiveIndex(index + 1);
      }

      // Auto-verify when all digits filled
      const filled = newDigits.every((d) => d !== "");
      if (filled) {
        const code = newDigits.join("");
        handleVerify(code);
      }
    },
    [digits]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === "Backspace") {
        if (digits[index]) {
          const newDigits = [...digits];
          newDigits[index] = "";
          setDigits(newDigits);
        } else if (index > 0) {
          setActiveIndex(index - 1);
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        setActiveIndex(index - 1);
      } else if (e.key === "ArrowRight" && index < 5) {
        setActiveIndex(index + 1);
      }
    },
    [digits]
  );

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const newDigits = [...digits];
    for (let i = 0; i < pasted.length && i < 6; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);

    if (pasted.length === 6) {
      handleVerify(pasted);
    } else {
      setActiveIndex(Math.min(pasted.length, 5));
    }
  }, [digits]);

  const handleVerify = async (code: string) => {
    if (verifying || verified) return;
    setVerifying(true);
    setError("");

    try {
      const success = await verifyOtp(code);
      if (success) {
        setVerified(true);
        // Redirect after brief success animation
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 1500);
      } else {
        setError(isAr ? "الرمز غير صحيح. حاول مرة أخرى." : "Invalid code. Please try again.");
        setVerifying(false);
      }
    } catch {
      setError(isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong. Try again.");
      setVerifying(false);
    }
  };

  const handleResend = () => {
    setCountdown(60);
    setCanResend(false);
    setError("");
    // Mock: just reset timer
  };

  const handleWrongNumber = () => {
    clearPendingAuth();
    const mode = (location.state as { mode?: string })?.mode;
    navigate(mode === "register" ? "/register" : "/login", { replace: true });
  };

  if (!pendingAuth) return null;

  const maskedPhone = `${pendingAuth.country_code} ${pendingAuth.phone.slice(0, 3)}***${pendingAuth.phone.slice(-3)}`;

  return (
    <div className="min-h-screen flex flex-col" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-20 md:py-28 bg-background-50">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            {verified ? (
              <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-accent-100 flex items-center justify-center animate-in zoom-in">
                <i className="ri-check-line text-3xl text-accent-500"></i>
              </div>
            ) : (
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
                <i className="ri-message-2-line text-2xl text-primary-500"></i>
              </div>
            )}

            {verified ? (
              <>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {isAr ? "تم التحقق!" : "Verified!"}
                </h1>
                <p className="text-sm text-foreground-500">
                  {isAr ? "جاري تحويلك..." : "Redirecting you..."}
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  {t("auth.enter_code")}
                </h1>
                <p className="text-sm text-foreground-500">
                  {isAr
                    ? `تم إرسال رمز مكون من 6 أرقام إلى ${maskedPhone}`
                    : `We sent a 6-digit code to ${maskedPhone}`}
                </p>
              </>
            )}
          </div>

          {!verified && (
            <div className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8">
              {/* OTP inputs */}
              <div
                className="flex justify-center gap-2 md:gap-3 mb-6"
                onPaste={handlePaste}
              >
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onFocus={() => setActiveIndex(index)}
                    className={`w-12 h-14 md:w-14 md:h-16 text-center text-xl font-bold rounded-xl border-2 transition-all outline-none ${
                      activeIndex === index
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : digit
                        ? "border-background-300 bg-background-100 text-foreground-900"
                        : "border-background-200 bg-background-100 text-foreground-900"
                    } ${verifying ? "opacity-60" : ""}`}
                    disabled={verifying}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              {/* Error */}
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  <p className="text-xs text-red-600">{error}</p>
                </div>
              )}

              {/* Verifying state */}
              {verifying && !error && (
                <div className="mb-5 flex items-center justify-center gap-2 text-sm text-foreground-500">
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>
                    {isAr ? "جاري التحقق..." : "Verifying..."}
                  </span>
                </div>
              )}

              {/* Resend */}
              <div className="text-center mb-5">
                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    {t("auth.resend_code")}
                  </button>
                ) : (
                  <p className="text-sm text-foreground-400">
                    {isAr
                      ? `إعادة الإرسال خلال ${countdown} ثانية`
                      : `Resend code in 0:${countdown.toString().padStart(2, "0")}`}
                  </p>
                )}
              </div>

              {/* Wrong number */}
              <div className="text-center pt-4 border-t border-background-200">
                <button
                  type="button"
                  onClick={handleWrongNumber}
                  className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer flex items-center gap-1 mx-auto"
                >
                  <i className="ri-arrow-left-line"></i>
                  {isAr ? "رقم خاطئ؟" : "Wrong number?"}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}